import React, { useState } from 'react';
import { Table, Button, Upload, Space, message, Modal, Switch, DatePicker, Form, Input } from 'antd';
import { UploadOutlined, DeleteOutlined, CopyOutlined, EyeOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';
import dayjs from 'dayjs';

interface MediaFileDto {
    id: string;
    originalName: string;
    fileName: string;
    contentType: string;
    size: number;
    filePath: string;
    isPublic: boolean;
    isActive: boolean;
    publishStartDate?: string;
    publishEndDate?: string;
    createdAt: string;
}

export const Media: React.FC = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
    const [form] = Form.useForm();

    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: ['adminMedia', page, pageSize, searchTerm],
        queryFn: async () => {
            const response = await apiClient.get('/admin/media', {
                params: { page, pageSize, searchTerm }
            });
            return response.data;
        }
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            await apiClient.delete(`/admin/media/${id}`);
        },
        onSuccess: () => {
            message.success('Media deleted successfully.');
            queryClient.invalidateQueries({ queryKey: ['adminMedia'] });
        },
        onError: () => {
            message.error('Failed to delete media.');
        }
    });

    const uploadMutation = useMutation({
        mutationFn: async (formData: FormData) => {
            await apiClient.post('/admin/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
        },
        onSuccess: () => {
            message.success('File uploaded successfully.');
            setIsUploadModalVisible(false);
            form.resetFields();
            queryClient.invalidateQueries({ queryKey: ['adminMedia'] });
        },
        onError: (error: any) => {
            message.error(error.response?.data?.detail || 'File upload failed.');
        }
    });

    const handleUploadSubmit = (values: any) => {
        if (!values.file || values.file.length === 0) {
            message.error('Please select a file to upload.');
            return;
        }

        const formData = new FormData();
        formData.append('file', values.file[0].originFileObj);
        formData.append('isPublic', values.isPublic ? 'true' : 'false');
        formData.append('isActive', values.isActive ? 'true' : 'false');
        if (values.requiredRoles) formData.append('requiredRoles', values.requiredRoles);
        if (values.dateRange) {
            if (values.dateRange[0]) formData.append('publishStartDate', values.dateRange[0].toISOString());
            if (values.dateRange[1]) formData.append('publishEndDate', values.dateRange[1].toISOString());
        }

        uploadMutation.mutate(formData);
    };

    const columns = [
        {
            title: 'Preview',
            key: 'preview',
            render: (_: any, record: MediaFileDto) => {
                const isImage = record.contentType.startsWith('image/');
                const url = record.isPublic ? `${record.filePath}` : `${record.id}`;
                return isImage ? (
                    <img src={url} alt={record.originalName} style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: '4px' }} />
                ) : (
                    <div style={{ width: 50, height: 50, background: '#e6f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>
                        DOC
                    </div>
                );
            }
        },
        { title: 'Original Name', dataIndex: 'originalName', key: 'originalName' },
        {
            title: 'Size',
            dataIndex: 'size',
            key: 'size',
            render: (size: number) => `${(size / 1024 / 1024).toFixed(2)} MB`
        },
        {
            title: 'Public',
            dataIndex: 'isPublic',
            key: 'isPublic',
            render: (isPublic: boolean) => <Switch checked={isPublic} disabled />
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm')
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: MediaFileDto) => (
                <Space size="middle">
                    <Button
                        icon={<CopyOutlined />}
                        onClick={() => {
                            const url = record.isPublic ? `${record.filePath}` : `${record.id}`;
                            navigator.clipboard.writeText(url);
                            message.info('URL copied to clipboard');
                        }}
                        title="Copy URL"
                    />
                    <Button
                        icon={<EyeOutlined />}
                        href={record.isPublic ? `${record.filePath}` : `${record.id}`}
                        target="_blank"
                        title="View File"
                    />
                    <Button danger icon={<DeleteOutlined />} onClick={() => {
                        Modal.confirm({
                            title: 'Are you sure you want to delete this file?',
                            onOk: () => deleteMutation.mutate(record.id)
                        });
                    }} />
                </Space>
            )
        }
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2>Media Library</h2>
                <Space>
                    <Input.Search 
                        placeholder="Search files..." 
                        onSearch={(value) => { setSearchTerm(value); setPage(1); }}
                        onChange={(e) => { if(!e.target.value) { setSearchTerm(''); setPage(1); } }}
                        allowClear 
                        style={{ width: 250 }}
                    />
                    <Button type="primary" icon={<UploadOutlined />} onClick={() => setIsUploadModalVisible(true)}>
                        Upload File
                    </Button>
                </Space>
            </div>

            <Table
                dataSource={data?.data || []}
                columns={columns}
                rowKey="id"
                loading={isLoading}
                pagination={{
                    current: data?.pageNumber || page,
                    pageSize: data?.pageSize || pageSize,
                    total: data?.totalRecords || 0,
                    onChange: (p, s) => { setPage(p); setPageSize(s); },
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`
                }}
            />

            <Modal
                title="Upload Media"
                open={isUploadModalVisible}
                onCancel={() => setIsUploadModalVisible(false)}
                footer={null}
                destroyOnHidden
            >
                <Form form={form} layout="vertical" onFinish={handleUploadSubmit} initialValues={{ isPublic: true, isActive: true }}>
                    <Form.Item name="file" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList} rules={[{ required: true, message: 'Please select a file' }]}>
                        <Upload beforeUpload={() => false} maxCount={1}>
                            <Button icon={<UploadOutlined />}>Select File</Button>
                        </Upload>
                    </Form.Item>

                    <Form.Item name="isPublic" valuePropName="checked" label="Is Public (Accessible without auth)">
                        <Switch />
                    </Form.Item>
                    <Form.Item name="isActive" valuePropName="checked" label="Is Active">
                        <Switch />
                    </Form.Item>
                    <Form.Item name="requiredRoles" label="Required Roles (Comma separated, optional)">
                        <Input placeholder="e.g. Admin, Editor" />
                    </Form.Item>
                    <Form.Item name="dateRange" label="Publish Date Range (Optional)">
                        <DatePicker.RangePicker showTime />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={uploadMutation.isPending} block>
                            Upload
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
